import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import moment from 'moment';
import Api from '../../api/global.service';
import CardContainer from '../../components/card/card-container.jsx';
import NoPagination from '../../components/pagination/noPagination.jsx';
import SearchColumn from '../../components/search/search-column.jsx';
import ListToolbar, { BtnNuevo } from '../../nuup/componentes/Toolbar/ListToolbar.jsx';
import TokensExternosTokenModal from './tokens-externos-token-modal.jsx';

const endpoint = 'admin/tokens_externos';
const url = '/admin/tokens';

const DIAS_AVISO_EXPIRACION = 15;

const btnAccion = { padding: '0.15rem 0.4rem', fontSize: '0.7rem', lineHeight: 1.2 };
const btnSecundario = { ...btnAccion, color: '#374E00', borderColor: '#374E00' };

const badgeEstado = item => {
  if (item.estado === 'revocado') return { clase: 'badge-danger', texto: 'Revocado' };
  if (item.estado === 'expirado') return { clase: 'badge-secondary', texto: 'Expirado' };

  if (item.expira_at && moment(item.expira_at).diff(moment(), 'days') <= DIAS_AVISO_EXPIRACION) {
    return { clase: 'badge-warning', texto: 'Expira pronto' };
  }

  return { clase: 'badge-success', texto: 'Activo' };
};

const TokensExternosList = () => {
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [blocking, setBlocking] = useState(false);
  const [tokenGenerado, setTokenGenerado] = useState(null);

  const cargar = useCallback(() => {
    setBlocking(true);
    Api.getAll(endpoint)
      .then(response => {
        const items = (response.data.data || []).map(item => ({
          ...item,
          expira_texto: item.expira_at ? moment(item.expira_at).format('DD/MM/YYYY') : 'Nunca',
          ultimo_uso_texto: item.ultimo_uso_at ? moment(item.ultimo_uso_at).format('DD/MM/YYYY HH:mm') : 'Sin uso',
        }));
        setData(items);
        setOriginalData(items);
        setBlocking(false);
      })
      .catch(() => {
        setBlocking(false);
        Swal.fire({ icon: 'error', title: '', text: 'No se pudieron cargar los tokens.' });
      });
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const confirmar = async (titulo, texto, textoBoton) => {
    const resultado = await Swal.fire({
      icon: 'warning',
      title: titulo,
      text: texto,
      showCancelButton: true,
      confirmButtonText: textoBoton,
      cancelButtonText: 'Cancelar',
    });
    return resultado.isConfirmed;
  };

  const handleRevocar = async item => {
    const ok = await confirmar(
      'Revocar token',
      `"${item.nombre}" dejará de funcionar de inmediato. Puedes reactivarlo después.`,
      'Revocar'
    );
    if (!ok) return;

    setBlocking(true);
    try {
      await Api.create(`${endpoint}/${item.uuid}/revocar`, {});
      cargar();
    } catch (e) {
      setBlocking(false);
      Swal.fire({ icon: 'error', title: '', text: 'No se pudo revocar el token.' });
    }
  };

  const handleReactivar = async item => {
    setBlocking(true);
    try {
      await Api.create(`${endpoint}/${item.uuid}/reactivar`, {});
      cargar();
    } catch (e) {
      setBlocking(false);
      Swal.fire({ icon: 'error', title: '', text: 'No se pudo reactivar el token.' });
    }
  };

  const handleRotar = async item => {
    const ok = await confirmar(
      'Re-generar token',
      `Se generará un secreto nuevo para "${item.nombre}" y el actual dejará de funcionar.`,
      'Re-generar'
    );
    if (!ok) return;

    setBlocking(true);
    try {
      const response = await Api.create(`${endpoint}/${item.uuid}/rotar`, {});
      setBlocking(false);
      setTokenGenerado(response.data.token);
      cargar();
    } catch (e) {
      setBlocking(false);
      Swal.fire({ icon: 'error', title: '', text: 'No se pudo re-generar el token.' });
    }
  };

  const handleEliminar = async item => {
    const ok = await confirmar(
      'Eliminar token',
      `"${item.nombre}" dejará de funcionar y desaparecerá del listado. Esta acción no se puede deshacer.`,
      'Eliminar'
    );
    if (!ok) return;

    setBlocking(true);
    try {
      await Api.delete(endpoint, item.uuid);
      cargar();
    } catch (e) {
      setBlocking(false);
      Swal.fire({ icon: 'error', title: '', text: 'No se pudo eliminar el token.' });
    }
  };

  const columns = React.useMemo(
    () => [
      { Header: 'NOMBRE', accessor: 'nombre', sortable: true },
      {
        Header: 'TOKEN',
        accessor: 'token_prefijo',
        sortable: true,
        Cell: cell => <code style={{ fontSize: '12px' }}>{cell.value}…</code>,
      },
      {
        // Cada tipo de integracion tiene una sola barrera de origen: dominio en
        // navegador, IP en servidor. Se muestra la que aplica, no las dos.
        Header: 'ORIGEN',
        accessor: 'dominios_permitidos',
        Cell: cell => {
          const item = cell.row.original;
          const esBackend = item.tipo_integracion === 'backend';
          const lista = (esBackend ? item.ips_permitidas : item.dominios_permitidos) || [];

          return (
            <span style={{ fontSize: '13px' }}>
              <span className="badge badge-secondary mr-1">{esBackend ? 'Servidor' : 'Navegador'}</span>
              {lista.join(', ')}
            </span>
          );
        },
      },
      {
        Header: 'URLS',
        accessor: 'urls_permitidas',
        Cell: cell => {
          const lista = cell.value || [];
          return (
            <span title={lista.join('\n')} style={{ whiteSpace: 'nowrap' }}>
              {lista.length === 0 ? <span className="text-danger">Ninguna</span> : `${lista.length} URL(s)`}
            </span>
          );
        },
      },
      { Header: 'EXPIRA', accessor: 'expira_texto', sortable: true },
      { Header: 'ÚLTIMO USO', accessor: 'ultimo_uso_texto', sortable: true },
      {
        Header: 'USOS',
        accessor: 'usos',
        sortable: true,
        Cell: cell => (
          <p className="text-right" style={{ margin: 0 }}>
            {cell.value}
          </p>
        ),
      },
      {
        Header: 'ESTADO',
        accessor: 'estado',
        sortable: true,
        Cell: cell => {
          const badge = badgeEstado(cell.row.original);
          return <span className={`badge ${badge.clase}`}>{badge.texto}</span>;
        },
      },
      {
        Header: 'ACCIONES',
        accessor: 'uuid',
        Cell: cell => {
          const item = cell.row.original;
          return (
            <div className="with-btn text-nowrap">
              <Link to={{ pathname: `${url}/edit/${item.uuid}` }} className="mr-2">
                <button type="button" className="btn btn-sm btn-outline-secondary btn-sirio" style={btnSecundario} title="Editar">
                  <i className="fa fa-pen" />
                </button>
              </Link>
              {item.estado === 'revocado' ? (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary btn-sirio mr-2"
                  style={btnSecundario}
                  title="Reactivar"
                  onClick={() => handleReactivar(item)}>
                  <i className="fa fa-undo" />
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary btn-sirio mr-2"
                  style={btnSecundario}
                  title="Revocar"
                  onClick={() => handleRevocar(item)}>
                  <i className="fa fa-ban" />
                </button>
              )}
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary btn-sirio mr-2"
                style={btnSecundario}
                title="Re-generar"
                onClick={() => handleRotar(item)}>
                <i className="fa fa-sync" />
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                style={btnAccion}
                title="Eliminar"
                onClick={() => handleEliminar(item)}>
                <i className="fa fa-trash" />
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  const searchColumns = React.useMemo(
    () => [
      { value: 0, target: '', label: 'TODO' },
      { value: 1, target: 'nombre', label: 'NOMBRE' },
      { value: 2, target: 'token_prefijo', label: 'TOKEN' },
      { value: 3, target: 'estado', label: 'ESTADO' },
    ],
    []
  );

  return (
    <div>
      <h1 className="page-header">Tokens externos</h1>

      <CardContainer height={'100%'}>
        <ListToolbar
          left={
            <SearchColumn className={'text-nowrap'} data={originalData} columns={searchColumns} setDataFilter={items => setData(items)} />
          }>
          <BtnNuevo show={true} to={{ pathname: `${url}/create` }}>
            Nuevo
          </BtnNuevo>
        </ListToolbar>

        <NoPagination blocking={blocking} columns={columns} data={data} />
      </CardContainer>

      <TokensExternosTokenModal abierto={tokenGenerado !== null} token={tokenGenerado || ''} onCerrar={() => setTokenGenerado(null)} />
    </div>
  );
};

export default TokensExternosList;
